import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Grid,
  TextField,
  Button,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import KeyIcon from '@mui/icons-material/Key';
import DashboardLayout from '../components/DashboardLayout';
import {
  getCurrentUser,
  updateProfile,
  joinOrganizationWithKey,
} from '../services/api';

const ProfilePage = ({ user, onLogout }) => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [signupKey, setSignupKey] = useState('');
  const [keyLoading, setKeyLoading] = useState(false);
  const [keyMessage, setKeyMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadProfileData();
  }, []);

  const loadProfileData = async () => {
    try {
      setLoading(true);
      // Fetch current user data from the API
      const userData = await getCurrentUser();
      setProfileData(userData);
      setEditData({
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        email: userData.email || '',
      });
    } catch (error) {
      console.error('Error loading profile:', error);
      setMessage({ type: 'error', text: 'Failed to load profile data' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditing(true);
    setMessage({ type: '', text: '' });
  };

  const handleCancel = () => {
    setEditing(false);
    setEditData({
      first_name: profileData?.first_name || '',
      last_name: profileData?.last_name || '',
      email: profileData?.email || '',
    });
    setMessage({ type: '', text: '' });
  };

  const handleSave = async () => {
    try {
      setSaveLoading(true);

      // Basic validation
      if (!editData.email || !editData.email.includes('@')) {
        setMessage({
          type: 'error',
          text: 'Please enter a valid email address',
        });
        return;
      }

      // Call the API to update the profile
      const updatedUser = await updateProfile(editData);

      // Update local state with the response
      setProfileData(updatedUser);

      setEditing(false);
      setMessage({ type: 'success', text: 'Profile updated successfully!' });

      // Clear success message after 3 seconds
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      console.error('Error updating profile:', error);

      // Handle different types of errors
      let errorMessage = 'Failed to update profile';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.email) {
          errorMessage = `Email: ${error.response.data.email.join(', ')}`;
        } else if (error.response.data.non_field_errors) {
          errorMessage = error.response.data.non_field_errors.join(', ');
        }
      }

      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const formatDate = dateString => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const handleJoinOrganization = async () => {
    if (!signupKey.trim()) {
      setKeyMessage({ type: 'error', text: 'Please enter a signup key' });
      return;
    }

    try {
      setKeyLoading(true);
      setKeyMessage({ type: '', text: '' });

      const result = await joinOrganizationWithKey(signupKey.trim());

      setKeyMessage({
        type: 'success',
        text: `Successfully joined ${result.organization.name}!`,
      });
      setSignupKey('');

      // Clear success message after 5 seconds
      setTimeout(() => {
        setKeyMessage({ type: '', text: '' });
      }, 5000);

      // Reload profile data to reflect new organization
      await loadProfileData();
    } catch (error) {
      console.error('Error joining organization:', error);

      let errorMessage = 'Failed to join organization';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.signup_key) {
          errorMessage = error.response.data.signup_key[0];
        } else if (error.response.data.non_field_errors) {
          errorMessage = error.response.data.non_field_errors[0];
        }
      }

      setKeyMessage({ type: 'error', text: errorMessage });
    } finally {
      setKeyLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout user={user} onLogout={onLogout}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user} onLogout={onLogout}>
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
          Profile Settings
        </Typography>

        {message.text && (
          <Alert
            severity={message.type}
            sx={{ mb: 3 }}
            onClose={() => setMessage({ type: '', text: '' })}
          >
            {message.text}
          </Alert>
        )}

        <Card elevation={2}>
          <CardContent sx={{ p: 4 }}>
            {/* Profile Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  mr: 3,
                  bgcolor: 'primary.main',
                  fontSize: '2rem',
                }}
              >
                <PersonIcon fontSize="large" />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                  {profileData?.first_name && profileData?.last_name
                    ? `${profileData.first_name} ${profileData.last_name}`
                    : profileData?.username || 'User'}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <EmailIcon fontSize="small" />
                  {profileData?.email}
                </Typography>
              </Box>
              <Box>
                {!editing ? (
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={handleEdit}
                  >
                    Edit Profile
                  </Button>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSave}
                      disabled={saveLoading}
                    >
                      {saveLoading ? <CircularProgress size={20} /> : 'Save'}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>

            <Divider sx={{ mb: 4 }} />

            {/* Profile Details */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={
                    editing
                      ? editData.first_name
                      : profileData?.first_name || 'N/A'
                  }
                  onChange={e =>
                    handleInputChange('first_name', e.target.value)
                  }
                  disabled={!editing}
                  variant={editing ? 'outlined' : 'filled'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={
                    editing
                      ? editData.last_name
                      : profileData?.last_name || 'N/A'
                  }
                  onChange={e => handleInputChange('last_name', e.target.value)}
                  disabled={!editing}
                  variant={editing ? 'outlined' : 'filled'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Username"
                  value={profileData?.username || 'N/A'}
                  disabled
                  variant="filled"
                  helperText="Username cannot be changed"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={editing ? editData.email : profileData?.email || 'N/A'}
                  onChange={e => handleInputChange('email', e.target.value)}
                  disabled={!editing}
                  variant={editing ? 'outlined' : 'filled'}
                  type="email"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* Account Information */}
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Account Information
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <CalendarTodayIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Date Joined
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(profileData?.date_joined)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <PersonIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2" fontWeight={600}>
                    User ID
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  #{profileData?.id}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* Join Organization Section */}
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Join Organization
            </Typography>

            {keyMessage.text && (
              <Alert
                severity={keyMessage.type}
                sx={{ mb: 3 }}
                onClose={() => setKeyMessage({ type: '', text: '' })}
              >
                {keyMessage.text}
              </Alert>
            )}

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter a signup key provided by an organization administrator to
                join their organization.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label="Signup Key"
                  value={signupKey}
                  onChange={e => setSignupKey(e.target.value)}
                  placeholder="Enter your signup key"
                  variant="outlined"
                  disabled={keyLoading}
                  InputProps={{
                    startAdornment: (
                      <KeyIcon sx={{ mr: 1, color: 'action.active' }} />
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleJoinOrganization}
                  disabled={keyLoading || !signupKey.trim()}
                  sx={{ minWidth: 120, height: 56 }}
                >
                  {keyLoading ? <CircularProgress size={24} /> : 'Join'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </DashboardLayout>
  );
};

export default ProfilePage;
